import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { blogSchema, blogPostingSchema, breadcrumbSchema, personSchema, webPageSchema } from "@/lib/seo";
import { withNetworkUtm } from "@/lib/network";
import { NetworkNextStep } from "@/components/NetworkNextStep";
const heroImage = "/img/perspectives-hero.webp";
void heroImage;

interface Article {
  title: string;
  category: string;
  excerpt: string;
  readTime: string;
  published: string;
  /** ISO publish date; must match the essay page's datePublished. */
  date: string;
  href: string;
}

const featured: Article = {
  title: "What Modern Psychiatry Gets Right (And What People Still Misunderstand)",
  category: "Psychiatry & Culture",
  excerpt:
    "A board-certified psychiatrist's educational essay on what modern psychiatry has gotten right, what the public conversation still misreads, and why the field deserves more nuance than the internet usually allows.",
  readTime: "14 min read",
  published: "May 2026",
  date: "2026-05-08",
  href: "/perspectives/what-modern-psychiatry-gets-right",
};

const articles: Article[] = [
  {
    title: "Why Your Mind Feels Loudest When You're Trying to Rest",
    category: "Anxiety & Overthinking",
    excerpt:
      "A board-certified psychiatrist's educational essay on why racing thoughts get louder at bedtime, why stillness amplifies the mind, and what's actually happening in the nervous system.",
    readTime: "8 min read",
    published: "May 2026",
    date: "2026-05-08",
    href: "/perspectives/why-your-mind-feels-loudest-when-trying-to-rest",
  },
  {
    title: "Why Thinking Harder Usually Makes Anxiety Worse",
    category: "Anxiety & Overthinking",
    excerpt:
      "Why analyzing anxiety doesn't relieve it, why intelligent people often overthink more, and how the mind confuses thinking with safety.",
    readTime: "8 min read",
    published: "May 2026",
    date: "2026-05-08",
    href: "/perspectives/why-thinking-harder-usually-makes-anxiety-worse",
  },
  {
    title: "Why Your Brain Replays Conversations Long After They End",
    category: "Anxiety & Overthinking",
    excerpt:
      "Why the brain replays old conversations, the social wiring underneath it, and why intelligent people get stuck in post-conversation analysis.",
    readTime: "7 min read",
    published: "May 2026",
    date: "2026-05-08",
    href: "/perspectives/why-your-brain-replays-conversations-long-after-they-end",
  },
  {
    title: "Why High Performers Can Still Feel Anxious",
    category: "Performance & Pressure",
    excerpt:
      "Why successful adults often carry anxiety underneath their performance, how achievement can deepen the pattern, and the wiring that drives it.",
    readTime: "9 min read",
    published: "May 2026",
    date: "2026-05-08",
    href: "/perspectives/why-high-performers-can-still-feel-anxious",
  },
  {
    title: "Why Your Nervous System Thinks Everything Is Urgent",
    category: "Nervous System & Stress",
    excerpt:
      "Why the modern nervous system reads ordinary life as continuous emergency, the wiring underneath chronic activation, and what the body actually means when it says urgent.",
    readTime: "9 min read",
    published: "May 2026",
    date: "2026-05-08",
    href: "/perspectives/why-your-nervous-system-thinks-everything-is-urgent",
  },
  {
    title: "Digital Mental Health Needs More Than Convenience",
    category: "Digital Mental Health",
    excerpt:
      "The convenience era of telehealth lowered the bar for access and, in places, the bar for care. A clinician's view on what the next chapter of digital mental health has to deliver.",
    readTime: "6 min read",
    published: "May 2026",
    date: "2026-05-07",
    href: "/perspectives/digital-mental-health-needs-more-than-convenience",
  },
  {
    title: "The Difference Between a Thought and Thinking",
    category: "Emotional Regulation",
    excerpt:
      "Most overthinking starts when a passing thought gets confused for a problem to solve. The distinction matters, and learning it changes the relationship you have with your own mind.",
    readTime: "8 min read",
    published: "May 2026",
    date: "2026-05-07",
    href: "/perspectives/difference-between-a-thought-and-thinking",
  },
  {
    title: "Why Popular Mental Health Commentary Falls Short in the Moment",
    category: "Mental Health Literacy",
    excerpt:
      "Most popular mental health commentary is calm-brain advice given to a panicked brain. Why that mismatch falls short so consistently, and what actually moves the needle when the moment is happening.",
    readTime: "8 min read",
    published: "May 2026",
    date: "2026-05-07",
    href: "/perspectives/why-popular-mental-health-commentary-falls-short-in-the-moment",
  },
  {
    title: "Why We Present Care as Five Options, Not One",
    category: "Mental Health Literacy",
    excerpt:
      "A psychiatrist's framework for the five distinct paths to mental health care, from self-guided education to crisis services. Route by need, not by default.",
    readTime: "10 min read",
    published: "July 2026",
    date: "2026-07-25",
    href: "/perspectives/why-we-present-care-as-five-options-not-one",
  },
];

const EDITORIAL_NOTE_1 =
  "The essays and perspectives on this website reflect educational and editorial viewpoints intended to encourage thoughtful discussion around psychiatry, emotional health, performance, digital mental health, and modern mental health literacy.";

const EDITORIAL_NOTE_2 =
  "They are not individualized medical advice, diagnosis, treatment, therapy, or a substitute for care from a licensed clinician.";

export default function Perspectives() {
  const canonical = "https://shariqrefai.com/perspectives/";
  const allArticles = [featured, ...articles];

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Perspectives by Shariq Refai, MD, MBA",
    itemListOrder: "https://schema.org/ItemListOrderDescending",
    numberOfItems: allArticles.length,
    itemListElement: allArticles.map((a, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://shariqrefai.com${a.href}`,
      name: a.title,
    })),
  };

  const slugFor = (href: string) => href.replace(/^\/perspectives\//, "");
  const blogPostings = allArticles.map((a) =>
    blogPostingSchema({
      slug: slugFor(a.href),
      title: a.title,
      description: a.excerpt,
      datePublished: a.date,
    }),
  );

  const blog = blogSchema(allArticles.map((a) => ({ slug: slugFor(a.href) })));

  const perspectivesPage = webPageSchema({
    path: "/perspectives",
    title: "Essays on Psychiatry, Anxiety & the Mind, Shariq Refai, MD",
    description:
      "Essays on psychiatry, anxiety, overthinking, and emotional health from board-certified psychiatrist Shariq Refai, MD, MBA. Clear, calm, and evidence-informed.",
    image: "/og-perspectives.jpg",
    type: "CollectionPage",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Perspectives", path: "/perspectives" },
    ],
    speakableSelectors: [".quick-answer"],
  });

  const schema = [
    personSchema,
    perspectivesPage,
    blog,
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Perspectives", path: "/perspectives" },
    ]),
    itemListSchema,
    ...blogPostings,
  ];

  const heroAlt =
    "Minimal monochrome editorial still life with books, pen, vase, and city light, used for Shariq Refai's Perspectives section.";

  return (
    <SiteLayout>
      <SEO
        title="Essays on Psychiatry, Anxiety & the Mind, Shariq Refai, MD"
        ogTitle="Perspectives by Shariq Refai, MD, MBA"
        twitterTitle="Perspectives by Shariq Refai, MD, MBA"
        description="Essays on psychiatry, anxiety, overthinking, and emotional health from board-certified psychiatrist Shariq Refai, MD, MBA. Clear, calm, and evidence-informed."
        url={canonical}
        image="/og-perspectives.jpg"
        type="website"
        schema={schema}
      />

      {/* Breadcrumbs */}
      <nav
        aria-label="Breadcrumb"
        className="container mx-auto max-w-6xl px-4 md:px-8 pt-6"
      >
        <ol className="flex items-center gap-2 text-xs tracking-widest uppercase text-muted-foreground font-medium list-none p-0">
          <li>
            <Link
              href="/"
              className="hover:text-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
            >
              Home
            </Link>
          </li>
          <li aria-hidden="true" className="text-border">
            /
          </li>
          <li className="text-primary" aria-current="page">
            Perspectives
          </li>
        </ol>
      </nav>

      {/* Hero */}
      <section
        aria-labelledby="perspectives-heading"
        className="container mx-auto max-w-6xl px-4 md:px-8 pt-10 pb-16 md:pt-14 md:pb-20"
      >
        <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-accent mb-5">
              Editorial Essays
            </p>
            <h1
              id="perspectives-heading"
              className="text-3xl md:text-5xl font-serif text-primary leading-[1.1] tracking-tight"
            >
              Perspectives on Psychiatry, Anxiety, and Overthinking
            </h1>
            <p className="mt-6 text-lg md:text-xl text-secondary font-light leading-relaxed max-w-xl">
              Essays and educational perspectives on psychiatry, anxiety,
              overthinking, performance, digital mental health, emotional
              regulation, and the patterns that shape how people think, feel,
              and function.
            </p>
            <p className="mt-5 text-sm tracking-widest uppercase text-muted-foreground font-medium">
              Thoughtful. Evidence-informed. Human.
            </p>
          </div>

          <div className="md:col-span-6 order-1 md:order-2">
            <figure className="relative">
              <img
                src={heroImage}
                alt={heroAlt}
                className="w-full h-auto object-cover aspect-[3/2] md:aspect-[4/3] shadow-sm"
                loading="eager"
                fetchPriority="high"
                width={1200}
                height={800}
                decoding="async"
              />
            </figure>
          </div>
        </div>
      </section>

      {/* Editorial intro + At a glance */}
      <section
        aria-label="About this section"
        className="container mx-auto max-w-6xl px-4 md:px-8 pb-12"
      >
        <div className="grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-7 border-l-2 border-accent pl-6 md:pl-8">
            <p className="text-base md:text-lg text-secondary font-light leading-relaxed">
              Written from the perspective of a board-certified psychiatrist,
              founder, author, and mental health educator, these articles
              explore emotional health with clinical grounding, clear language,
              and human context.
            </p>
            <p className="mt-4 text-sm text-muted-foreground font-light leading-relaxed">
              Based in Jacksonville, Florida, Shariq Refai, MD, MBA writes
              about psychiatry, digital mental health, performance, and
              emotional health.
            </p>
            <p className="mt-4 text-sm text-muted-foreground font-light leading-relaxed">
              These essays are the educational voice. For labeled opinion on
              how the profession itself is trained, paid, and practiced, see
              the{" "}
              <a
                href={withNetworkUtm("https://shrinkiatry.com/opinion/", "perspectives_hub")}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:underline"
              >
                opinion column at shrinkiatry
              </a>
              .
            </p>
          </div>

          <div
            aria-label="At a glance"
            className="md:col-span-5 bg-[#EFECE6] p-6 md:p-7"
          >
            <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-3">
              At a Glance
            </p>
            <p className="text-sm text-secondary font-light leading-relaxed">
              Perspectives is the educational essay section of shariqrefai.com,
              featuring writing from Shariq Refai, MD, MBA on psychiatry,
              anxiety, overthinking, performance, digital mental health,
              emotional regulation, and modern mental health literacy.
            </p>
            <p className="text-xs text-secondary font-light leading-relaxed mt-4 pt-4 border-t border-border/60">
              <strong className="text-primary font-medium">Editorial independence:</strong>{" "}
              {EDITORIAL_NOTE_1} {EDITORIAL_NOTE_2}
            </p>
          </div>
        </div>
      </section>

      {/* Featured essay */}
      <section
        aria-labelledby="featured-heading"
        className="container mx-auto max-w-6xl px-4 md:px-8 pt-8 pb-16"
      >
        <div className="flex items-end justify-between border-b border-border pb-4 mb-10">
          <h2
            id="featured-heading"
            className="text-xs font-semibold tracking-[0.25em] uppercase text-muted-foreground"
          >
            Featured Essay
          </h2>
        </div>

        <Link
          href={featured.href}
          aria-label={`Read essay: ${featured.title}`}
          className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-4 focus-visible:ring-offset-background rounded-sm"
        >
          <article className="grid md:grid-cols-12 gap-8 md:gap-12 items-start border-t border-border pt-10">
            <div className="md:col-span-3">
              <p className="text-xs font-semibold tracking-widest uppercase text-accent">
                {featured.category}
              </p>
              <p className="mt-3 text-xs tracking-widest uppercase text-muted-foreground">
                {featured.published} · {featured.readTime}
              </p>
            </div>
            <div className="md:col-span-9">
              <h3 className="text-3xl md:text-4xl font-serif text-primary leading-[1.15] tracking-tight group-hover:text-accent transition-colors">
                {featured.title}
              </h3>
              <p className="mt-5 text-base md:text-lg text-secondary font-light leading-relaxed max-w-3xl">
                {featured.excerpt}
              </p>
              <span className="mt-6 inline-block text-sm font-medium tracking-wide text-accent group-hover:text-primary transition-colors">
                Read essay &rarr;
              </span>
            </div>
          </article>
        </Link>
      </section>

      {/* Article grid */}
      <section
        aria-labelledby="essays-heading"
        className="container mx-auto max-w-6xl px-4 md:px-8 pb-24"
      >
        <div className="flex items-end justify-between border-b border-border pb-4 mb-10">
          <h2
            id="essays-heading"
            className="text-xs font-semibold tracking-[0.25em] uppercase text-muted-foreground"
          >
            All Essays
          </h2>
        </div>

        <ul className="grid md:grid-cols-2 gap-8 lg:gap-10 list-none p-0">
          {articles.map((article, i) => (
            <li key={i}>
              <Link
                href={article.href}
                aria-label={`Read essay: ${article.title}`}
                className="group block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-4 focus-visible:ring-offset-background rounded-sm"
              >
                <article className="flex flex-col h-full p-8 md:p-9 bg-card border border-border group-hover:border-accent transition-colors">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-xs font-semibold tracking-widest uppercase text-accent">
                      {article.category}
                    </span>
                    <span className="text-xs tracking-widest uppercase text-muted-foreground">
                      {article.readTime}
                    </span>
                  </div>
                  <h3 className="text-2xl font-serif text-primary leading-tight group-hover:text-accent transition-colors">
                    {article.title}
                  </h3>
                  <p className="mt-4 text-base text-secondary font-light leading-relaxed">
                    {article.excerpt}
                  </p>
                  <div className="mt-auto pt-8 flex items-center justify-between">
                    <span className="text-xs tracking-widest uppercase text-muted-foreground">
                      {article.published}
                    </span>
                    <span className="text-sm font-medium tracking-wide text-accent group-hover:text-primary transition-colors">
                      Read essay &rarr;
                    </span>
                  </div>
                </article>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Editorial Mission */}
      <section className="editorial-mission container mx-auto max-w-3xl px-4 md:px-8 pb-12">
        <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
          Editorial Mission
        </h2>
        <p className="text-secondary font-light leading-relaxed mb-4">
          Perspectives is the editorial essay section of shariqrefai.com.
          The work here is general public education on psychiatry, anxiety,
          overthinking, performance, digital mental health, emotional
          regulation, and modern mental health literacy. Every essay is
          authored and editorially reviewed by Shariq Refai, MD, MBA,
          board-certified psychiatrist.
        </p>
        <p className="text-secondary font-light leading-relaxed">
          The essays aren't individualized medical advice, diagnosis,
          treatment, or therapy, and aren't a substitute for care from a
          licensed clinician. For clinical care inquiries, visit{" "}
          <a
            href={withNetworkUtm("https://shrinkmd.com/", "perspectives_page")}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
          >
            shrinkmd.com
          </a>
          .
        </p>
      </section>

      {/* Browse by Topic */}
      <section className="browse-by-topic container mx-auto max-w-3xl px-4 md:px-8 pb-12">
        <h2 className="text-2xl md:text-3xl font-serif text-primary mb-6">
          Browse by Topic
        </h2>
        <div className="grid sm:grid-cols-2 gap-8">
          <div>
            <h3 className="font-serif text-lg text-primary mb-3">
              Anxiety and Overthinking
            </h3>
            <ul className="list-none p-0 grid gap-2 text-secondary font-light">
              <li>
                <Link
                  href="/perspectives/why-your-mind-feels-loudest-when-trying-to-rest"
                  className="hover:text-accent underline decoration-accent/30 underline-offset-4"
                >
                  Why Your Mind Feels Loudest When You're Trying to Rest
                </Link>
              </li>
              <li>
                <Link
                  href="/perspectives/why-thinking-harder-usually-makes-anxiety-worse"
                  className="hover:text-accent underline decoration-accent/30 underline-offset-4"
                >
                  Why Thinking Harder Usually Makes Anxiety Worse
                </Link>
              </li>
              <li>
                <Link
                  href="/perspectives/why-high-performers-can-still-feel-anxious"
                  className="hover:text-accent underline decoration-accent/30 underline-offset-4"
                >
                  Why High Performers Can Still Feel Anxious
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-serif text-lg text-primary mb-3">
              The Nervous System
            </h3>
            <ul className="list-none p-0 grid gap-2 text-secondary font-light">
              <li>
                <Link
                  href="/perspectives/why-your-nervous-system-thinks-everything-is-urgent"
                  className="hover:text-accent underline decoration-accent/30 underline-offset-4"
                >
                  Why Your Nervous System Thinks Everything Is Urgent
                </Link>
              </li>
              <li>
                <Link
                  href="/perspectives/why-your-mind-feels-loudest-when-trying-to-rest"
                  className="hover:text-accent underline decoration-accent/30 underline-offset-4"
                >
                  Why Your Mind Feels Loudest When You're Trying to Rest
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-serif text-lg text-primary mb-3">
              Thoughts and Cognition
            </h3>
            <ul className="list-none p-0 grid gap-2 text-secondary font-light">
              <li>
                <Link
                  href="/perspectives/difference-between-a-thought-and-thinking"
                  className="hover:text-accent underline decoration-accent/30 underline-offset-4"
                >
                  The Difference Between a Thought and Thinking
                </Link>
              </li>
              <li>
                <Link
                  href="/perspectives/why-your-brain-replays-conversations-long-after-they-end"
                  className="hover:text-accent underline decoration-accent/30 underline-offset-4"
                >
                  Why Your Brain Replays Conversations Long After They End
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-serif text-lg text-primary mb-3">
              Modern Psychiatry and Digital Mental Health
            </h3>
            <ul className="list-none p-0 grid gap-2 text-secondary font-light">
              <li>
                <Link
                  href="/perspectives/what-modern-psychiatry-gets-right"
                  className="hover:text-accent underline decoration-accent/30 underline-offset-4"
                >
                  What Modern Psychiatry Gets Right
                </Link>
              </li>
              <li>
                <Link
                  href="/perspectives/digital-mental-health-needs-more-than-convenience"
                  className="hover:text-accent underline decoration-accent/30 underline-offset-4"
                >
                  Digital Mental Health Needs More Than Convenience
                </Link>
              </li>
              <li>
                <Link
                  href="/perspectives/why-popular-mental-health-commentary-falls-short-in-the-moment"
                  className="hover:text-accent underline decoration-accent/30 underline-offset-4"
                >
                  Why Popular Mental Health Commentary Falls Short in the
                  Moment
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* How to Read These Essays */}
      <section className="how-to-read container mx-auto max-w-3xl px-4 md:px-8 pb-12">
        <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
          How to Read These Essays
        </h2>
        <p className="text-secondary font-light leading-relaxed mb-4">
          Each essay opens with a Quick Answer block summarizing the core
          point in three or four sentences. If you only have a minute, read
          the Quick Answer. If you want the full argument, read the article
          body. If you have specific questions, scroll to the FAQ at the
          bottom of each piece. Every essay also includes a References
          section if it draws on primary sources.
        </p>
        <p className="text-secondary font-light leading-relaxed">
          The essays are designed to be useful on their own and to link
          together. The Related Perspectives block at the bottom of each
          article points to two or three other essays that develop adjacent
          ideas.
        </p>
      </section>

      {/* Resources for Specific Conditions */}
      <section className="resources-conditions container mx-auto max-w-3xl px-4 md:px-8 pb-12">
        <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
          Resources for Specific Conditions
        </h2>
        <p className="text-secondary font-light leading-relaxed mb-4">
          For deeper, condition-specific reading, Dr. Refai serves as
          medical reviewer for four independent editorial publications:
        </p>
        <ul className="list-disc pl-6 space-y-2 text-secondary font-light leading-relaxed mb-4">
          <li>
            <a
              href={withNetworkUtm("https://anxietyresource.org/", "perspectives_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              <strong>AnxietyResource.org</strong>
            </a>
            , a clinically reviewed editorial publication on anxiety, with
            a 50-entry glossary and state-by-state directories.
          </li>
          <li>
            <a
              href={withNetworkUtm("https://depressionresource.org/", "perspectives_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              <strong>DepressionResource.org</strong>
            </a>
            , a long-form depression library including screening tools,
            safety plan templates, and crisis resources.
          </li>
          <li>
            <a
              href={withNetworkUtm("https://anxietyresearch.org/", "perspectives_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              <strong>AnxietyResearch.org</strong>
            </a>
            , an editorial publication translating current anxiety research
            into plain-language summaries for general readers.
          </li>
          <li>
            <a
              href={withNetworkUtm("https://psychiatryrx.org/", "perspectives_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              <strong>PsychiatryRx.org</strong>
            </a>
            , a plain-language reference site of psychiatrist-reviewed guides
            to psychiatric and sleep medications.
          </li>
        </ul>
        <p className="text-secondary font-light leading-relaxed">
          All four are editorial and educational. None is a clinical service.
        </p>
      </section>

      {/* About the Editor */}
      <section className="about-editor container mx-auto max-w-3xl px-4 md:px-8 pb-12">
        <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
          About the Editor
        </h2>
        <p className="text-secondary font-light leading-relaxed">
          Shariq Refai, MD, MBA, is a board-certified psychiatrist with
          dual certifications from the American Board of Psychiatry and
          Neurology and the American Board of Sports and Performance
          Psychiatry. He's a Fellow of the American Psychiatric Association
          and a member of the Forbes Business Council. He founded{" "}
          <a
            href={withNetworkUtm("https://shrinkmd.com/", "perspectives_page")}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
          >
            shrinkMD
          </a>
          , the separate multistate telepsychiatry practice. He's based in
          Jacksonville, Florida.{" "}
          <Link
            href="/about"
            className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
          >
            Read his full bio.
          </Link>
        </p>
      </section>

      {/* Publishing Cadence */}
      <section className="publishing-cadence container mx-auto max-w-3xl px-4 md:px-8 pb-12">
        <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
          Publishing Cadence
        </h2>
        <p className="text-secondary font-light leading-relaxed">
          New essays are published as they're written and edited. Each
          essay carries a published date and a last-reviewed date.
          Existing essays are reviewed for accuracy at least once per year,
          with the last-reviewed date updated when meaningful changes are
          made.
        </p>
      </section>

      {/* Educational disclaimer above footer */}
      <div
        role="note"
        aria-label="Educational disclaimer"
        className="container mx-auto max-w-6xl px-4 md:px-8 pb-16"
      >
        <div className="border border-border bg-muted/40 p-6 text-sm text-secondary font-light leading-relaxed">
          <strong className="font-medium text-primary block mb-2">
            Educational Disclaimer
          </strong>
          The articles on this page are for general educational and
          informational purposes only and do not provide medical advice,
          diagnosis, treatment, therapy, or a physician-patient relationship.
        </div>
      </div>
    
      <section className="bg-background py-20 border-t border-border">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <NetworkNextStep
            campaign="perspectives_next_step"
            destinations={["shrinkmd", "shrinq", "shrinkopedia"]}
          />
        </div>
      </section>
    </SiteLayout>
  );
}
